import { Injectable, inject } from '@angular/core';
import { forkJoin, from, Observable, of, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { MarketFeathersService } from './market-feathers.service';

export interface ClientConnectDialog {
  type: 'client-connect';
  /** Market client id (typeId in feathers path/query). */
  typeId: number;
  /** Participant user id (extraTypeId). */
  extraTypeId: number;
  /** Required for SUPPORT scoping in client-connect. */
  distributorId?: number;
  clientName?: string;
  clientInn?: string;
}

export interface ChatMessageQuoteUser {
  id?: number;
  name?: string;
  surname?: string;
  patronymic?: string;
}

export interface ChatMessageQuoteRef {
  id?: number;
  linkId?: number;
  message?: {
    id?: number;
    text?: string;
    user?: ChatMessageQuoteUser;
  };
}

export interface ChatMessageTicket {
  id?: number;
  status?: string;
  youtrackIssueKey?: string;
}

export interface ChatMessageItem {
  id: number;
  text?: string;
  createdAt?: string;
  updatedAt?: string;
  userId?: number;
  replyUserId?: number;
  type?: string;
  typeId?: number | string;
  extraTypeId?: number | string;
  distributorId?: number | string;
  addressId?: number | string;
  senderAddressId?: number | string | null;
  readAt?: string | null;
  isSystem?: boolean;
  isTicketInfo?: boolean;
  isTicketProcessInfo?: boolean;
  isApprovedTask?: boolean;
  ticketId?: number | string | null;
  ticket?: ChatMessageTicket | null;
  disableForward?: boolean;
  user?: ChatMessageQuoteUser;
  attached?: {
    messages?: ChatMessageQuoteRef[];
    uploads?: unknown[];
  };
  [key: string]: unknown;
}

@Injectable({ providedIn: 'root' })
export class ClientConnectChatService {
  private readonly feathers = inject(MarketFeathersService);

  private dialog: ClientConnectDialog | null = null;

  open(dialog: ClientConnectDialog): void {
    this.dialog = {
      type: 'client-connect',
      typeId: Number(dialog.typeId),
      extraTypeId: Number(dialog.extraTypeId),
      distributorId: dialog.distributorId != null ? Number(dialog.distributorId) : undefined,
      clientName: dialog.clientName,
      clientInn: dialog.clientInn,
    };
  }

  close(): void {
    this.dialog = null;
  }

  get activeDialog(): ClientConnectDialog | null {
    return this.dialog;
  }

  getMessages(page = 0, limit = 50): Observable<ChatMessageItem[]> {
    const dialog = this.requireDialog();
    const service = this.messagesService(dialog);

    const query: Record<string, unknown> = {
      $limit: limit,
      $skip: page * limit,
      $sort: { createdAt: -1 },
      participant: dialog.extraTypeId,
      type: dialog.type,
      typeId: dialog.typeId,
      ...this.communityQuery(dialog),
    };

    return from(service.find({ query }) as Promise<{ data: ChatMessageItem[] }>).pipe(
      map((result) => [...(result?.data || [])].reverse())
    );
  }

  sendMessage(text: string, quote?: ChatMessageItem | null): Observable<ChatMessageItem> {
    const dialog = this.requireDialog();
    const trimmed = text?.trim() ?? '';
    if (!trimmed && !quote) {
      return throwError(() => new Error('Empty message'));
    }

    const service = this.messagesService(dialog);
    const quoteMessages = quote ? [quote] : [];

    return this.createMessageLinks(quoteMessages, dialog.type).pipe(
      switchMap((messages) =>
        from(
          service.create(
            {
              text: trimmed,
              attached: { messages, uploads: [] },
            },
            { query: this.communityQuery(dialog) }
          ) as Promise<ChatMessageItem>
        )
      )
    );
  }

  editMessage(messageId: number, text: string, attached?: ChatMessageItem['attached']): Observable<ChatMessageItem> {
    const dialog = this.requireDialog();
    const service = this.messagesService(dialog);
    const payload: Record<string, unknown> = { text: text.trim() };
    if (attached) {
      payload.attached = attached;
    }

    return from(
      service.patch(messageId, payload, { query: this.communityQuery(dialog) }) as Promise<ChatMessageItem>
    );
  }

  markRead(messageId: number): Observable<ChatMessageItem> {
    const dialog = this.requireDialog();
    const service = this.messagesService(dialog);
    return from(
      service.patch(messageId, { readAt: new Date().toISOString() }, { query: this.communityQuery(dialog) }) as Promise<ChatMessageItem>
    );
  }

  removeMessage(messageId: number): Observable<ChatMessageItem> {
    const dialog = this.requireDialog();
    const service = this.messagesService(dialog);
    return from(service.remove(messageId, { query: this.communityQuery(dialog) }) as Promise<ChatMessageItem>);
  }

  onMessageCreated(handler: (msg: ChatMessageItem) => void): () => void {
    return this.onMessageEvent('created', handler);
  }

  onMessageRemoved(handler: (msg: ChatMessageItem) => void): () => void {
    return this.onMessageEvent('removed', handler);
  }

  onMessagePatched(handler: (msg: ChatMessageItem) => void): () => void {
    return this.onMessageEvent('patched', handler);
  }

  private createMessageLinks(
    messages: ChatMessageItem[],
    serviceType: string
  ): Observable<Array<{ id: number; service: string }>> {
    if (!messages.length) {
      return of([]);
    }

    return forkJoin(
      messages.map((message) => {
        const linkType =
          message.type === 'task-comments' || String(message.type || '').includes('chat') ? 'users-chat' : 'messages';

        return from(
          this.feathers.service('message-links').create({
            linkId: message.id,
            type: linkType,
          }) as Promise<{ id: number }>
        ).pipe(map((link) => ({ id: link.id, service: serviceType })));
      })
    );
  }

  private onMessageEvent(
    event: 'created' | 'removed' | 'patched',
    handler: (msg: ChatMessageItem) => void
  ): () => void {
    const dialog = this.dialog;
    if (!dialog) {
      return () => undefined;
    }

    const service = this.messagesService(dialog);
    const listener = (msg: ChatMessageItem) => {
      if (this.isMessageForActiveDialog(msg, dialog)) {
        handler(msg);
      }
    };

    service.on(event, listener);
    return () => {
      try {
        service.removeListener(event, listener);
      } catch {
        /* ignore */
      }
    };
  }

  private messagesService(dialog: ClientConnectDialog): any {
    return this.feathers.service(`messages/${dialog.type}/${dialog.typeId}`);
  }

  private communityQuery(dialog: ClientConnectDialog): { extraTypeId: number; distributorId?: number } {
    const query: { extraTypeId: number; distributorId?: number } = {
      extraTypeId: dialog.extraTypeId,
    };
    if (dialog.distributorId != null && !Number.isNaN(dialog.distributorId)) {
      query.distributorId = dialog.distributorId;
    }
    return query;
  }

  private isMessageForActiveDialog(msg: ChatMessageItem, dialog: ClientConnectDialog): boolean {
    if (!msg) {
      return false;
    }

    if (msg.type != null && String(msg.type) !== dialog.type) {
      return false;
    }

    if (msg.typeId != null && Number(msg.typeId) !== Number(dialog.typeId)) {
      return false;
    }

    if (msg.extraTypeId != null && Number(msg.extraTypeId) !== Number(dialog.extraTypeId)) {
      return false;
    }

    if (
      dialog.distributorId != null &&
      msg.distributorId != null &&
      Number(msg.distributorId) !== Number(dialog.distributorId)
    ) {
      return false;
    }

    return true;
  }

  private requireDialog(): ClientConnectDialog {
    if (!this.dialog) {
      throw new Error('Client-connect chat is not open');
    }
    if (!this.dialog.type || this.dialog.type !== 'client-connect') {
      throw new Error('Invalid chat type');
    }
    if (this.dialog.typeId == null || Number.isNaN(Number(this.dialog.typeId))) {
      throw new Error('Invalid chat typeId (market client id)');
    }
    if (this.dialog.extraTypeId == null || Number.isNaN(Number(this.dialog.extraTypeId))) {
      throw new Error('Invalid chat extraTypeId (user id)');
    }
    return this.dialog;
  }
}
